/**
 * The sounds an order makes: one for arriving, a quieter one for changing.
 *
 * Synthesised rather than played from files: they are short notes, and audio assets would be more
 * things to ship, cache-bust and keep for something that is a few lines of maths. It also means a
 * chime cannot fail to load at the one moment it is needed.
 *
 * Browsers refuse to make noise until the page has been interacted with, which is why the context is
 * created on the click that turns the sound on — the question the orders screen asks is what arms
 * this, not the first order to arrive.
 */
type TyNote = { at: number; hz: number; gain: number };

let context: AudioContext | null = null;

const play = (notes: TyNote[]): void => {
    try {
        if (!context) return;
        void context.resume();

        for (const note of notes) {
            const start = context.currentTime + note.at;
            const oscillator = context.createOscillator();
            const gain = context.createGain();

            oscillator.type = "sine";
            oscillator.frequency.value = note.hz;

            // Shaped rather than switched: a square-edged note clicks on cheap counter speakers.
            gain.gain.setValueAtTime(0, start);
            gain.gain.linearRampToValueAtTime(note.gain, start + 0.015);
            gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.22);

            oscillator.connect(gain).connect(context.destination);
            oscillator.start(start);
            oscillator.stop(start + 0.24);
        }
    } catch {
        // A silent failure is the right one here: a missed chime must never break the screen.
    }
};

/** Called from the click that enables the sound, so the context is created inside a real gesture. */
export function armOrderChime(): void {
    try {
        context = context ?? new AudioContext();
        void context.resume();
    } catch {
        // No Web Audio means no chime. The snack is still there, and it is the part that matters.
    }
}

/** A new order: two notes rising a fifth. Short and soft — nothing like a phone ringing in a café. */
export function playOrderChime(): void {
    play([
        { at: 0, hz: 880, gain: 0.14 },
        { at: 0.14, hz: 1320, gain: 0.14 },
    ]);
}

/**
 * An order that changed: one note, lower and quieter than an arrival's.
 *
 * Deliberately a different sound rather than the same one again. A counter learns these two apart in
 * a shift, and "something new came in" and "something you already have moved" are answered by
 * different people — telling them apart without looking up is the whole point of a sound.
 */
export function playOrderUpdateChime(): void {
    play([{ at: 0, hz: 660, gain: 0.1 }]);
}
